/* eslint-disable max-lines */
import { Breadcrumbs } from '@components/atoms/ui-kit/breadcrumbs.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { InfoBanner } from '@components/atoms/ui-kit/info-banner.tsx';
import { InfoTooltip } from '@components/atoms/ui-kit/info-tooltip.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';

import {
  MAX_DATASET_FILES_PER_BATCH,
  SUPPORTED_AUDIO_FORMATS,
  SUPPORTED_TRANSCRIPT_FORMATS,
  SUPPORTED_VIDEO_FORMATS,
  useCreateDataset
} from '@utils/api/custom-models/use-create-dataset.tsx';
import { UppyDashboard } from '@components/shared/upload/uppy-dashboard.tsx';
import { useDeleteTranscriptCustomModel } from '@utils/api/custom-models/use-delete-transcript.tsx';
import { useDeleteCustomModel } from '@utils/api/custom-models/use-delete-custom-model.tsx';
import { useDownloadDatasetCustomModel } from '@utils/api/custom-models/use-download-dataset.tsx';
import {
  downloadDatasetExport,
  useExportCustomModelDataset
} from '@utils/api/custom-models/use-export-dataset.tsx';
import { useGetCustomModel } from '@utils/api/custom-models/use-get-custom-model.tsx';
import {
  CustomModelDataStatus,
  ModelData,
  useGetCustomModelsData
} from '@utils/api/custom-models/use-get-custom-models-data.tsx';
import { useGetContributions } from '@utils/api/custom-models/use-get-contributions.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useRunCustomModel } from '@utils/api/custom-models/use-train-language.tsx';
import {
  CustomModelDataType,
  useUpdateModel
} from '@utils/api/custom-models/use-update-custom-model.tsx';
import {
  useCustomModelReadiness,
  useValidateCustomModel
} from '@utils/api/custom-models/use-validate-custom-model.tsx';
import {
  TrainingRound,
  useGetTrainingRounds
} from '@utils/api/custom-models/use-get-training-rounds.tsx';
import { ContributionsList } from '@components/custom-models/contributions-list.tsx';
import { ReadinessCard } from '@components/custom-models/readiness-card.tsx';
import { RoundAllowanceCard } from '@components/custom-models/round-allowance-card.tsx';
import { useCurrentCustomModelAllowance } from '@utils/api/subscriptions/use-current-custom-model-allowance.tsx';
import { useGetModels } from '@utils/api/models/use-get-models.ts';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { buildCustomModelBreadcrumbTrail } from '@utils/breadcrump-trails/build-custom-model-breadcrumb.ts';
import { formatDate } from '@utils/date.ts';
import { BaseError } from '@utils/types/shared/base';

import { AxiosError } from 'axios';
import {
  AlertCircle,
  Download,
  FileDown,
  Loader2,
  Plus,
  RefreshCcw,
  Save,
  Trash2
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '../../router/paths.ts';
import { setContributionTarget } from '@utils/localStorage/contribution-target.ts';
import { isContribution } from '@utils/editors/contribution-messages.ts';

type Params = { modelId: string };

type DatasetRow = {
  id: number;
  name: string;
  type: string;
  language_id: number | null;
  media_url: string | null;
  duration: number | null;
  url: string;
  start_time: number | null;
  end_time: number | null;
  status: CustomModelDataStatus;
  error: string | null;
  round_number: number | null;
};

type RoundRow = {
  id: number;
  round_number: number;
  training_status: number;
  started_at: string | null;
  finished_at: string | null;
  asr_version: string | null;
  wer: number | null;
  log_name: string | null;
  data_count: number | null;
};

// Per-file processing status. Uploads land as PROCESSING (background transcode
// + storage upload) and flip to READY, or FAILED with a hover-able reason.
const DatasetStatusCell = ({
  status,
  error
}: {
  status: CustomModelDataStatus;
  error: string | null;
}) => {
  const { t } = useTranslation();

  if (status === CustomModelDataStatus.PROCESSING) {
    return (
      <Badge color="amber" className="whitespace-nowrap">
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('dataset-status-processing', { defaultValue: 'Processing' })}
      </Badge>
    );
  }

  if (status === CustomModelDataStatus.FAILED) {
    const reason =
      error ||
      t('dataset-status-failed-generic', { defaultValue: 'Processing failed' });
    return (
      <Tooltip label={reason} placement="top">
        <Badge color="red" className="cursor-help whitespace-nowrap">
          <AlertCircle className="h-3 w-3" />
          {t('dataset-status-failed', { defaultValue: 'Failed' })}
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Badge color="emerald" className="whitespace-nowrap">
      <span className="inline-block h-2 w-2 rounded-full bg-current opacity-70" />
      {t('dataset-status-ready', { defaultValue: 'Ready' })}
    </Badge>
  );
};

const RoundStatusBadge = ({ status }: { status: number }) => {
  const { t } = useTranslation();
  const colorMap: Record<number, 'zinc' | 'amber' | 'sky' | 'emerald' | 'red'> =
    {
      1: 'zinc',
      2: 'amber',
      3: 'sky',
      4: 'emerald',
      5: 'red'
    };
  const labelMap: Record<number, string> = {
    1: t('not-running'),
    2: t('provisioning-resources'),
    3: t('running'),
    4: t('success'),
    5: t('failed')
  };
  return (
    <Badge color={colorMap[status] ?? 'zinc'}>
      {labelMap[status] ?? String(status)}
    </Badge>
  );
};

export const UpdateCustomModel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { modelId } = useParams<Params>();

  const [name, setName] = useState('');
  const [newDatasetModal, setNewDatasetModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(
    null
  );
  const [datasetType, setDatasetType] = useState<CustomModelDataType | 'auto'>(
    'auto'
  );
  const [trainTheLanguageModal, setTrainTheLanguageModal] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [skipConfirmModal, setSkipConfirmModal] = useState(false);
  const [skippedFiles, setSkippedFiles] = useState<string[]>([]);
  // Delete-model flow (#2211). Contributed clips are hand-curated and their
  // source sessions may already have aged out — deleting them destroys data
  // that cannot be rebuilt. The confirmation asks the customer to acknowledge
  // that, and offers the whole-dataset export first.
  const [deleteModelModalOpen, setDeleteModelModalOpen] = useState(false);
  const [acknowledgeContributions, setAcknowledgeContributions] =
    useState(false);
  const [isDeletingModel, setIsDeletingModel] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { data } = useGetCustomModel(Number(modelId));
  // Opening a model's page makes it where the editor's "Add to training
  // data" sends clips. Without this the editor kept an older choice and the
  // customer's corrections landed on a different model.
  useEffect(() => {
    setContributionTarget(Number(modelId));
  }, [modelId]);
  const { data: organization } = useOrganization();
  const {
    data: customModelData,
    isLoading: isLoadingData,
    isFetching
  } = useGetCustomModelsData(Number(modelId));
  // Contributions have their own dedicated fetch — the shared datasets
  // query above pages off the URL (default limit 25) which meant the
  // Contributions tab silently truncated at 25 rows once a model had
  // more than that mixed in. This fetch pulls the full contribution set
  // (up to 5000 rows) regardless of Datasets tab pagination.
  const { data: contributionsData } = useGetContributions(Number(modelId));
  const { data: supportedLanguages } = useGetModels();
  const { data: roundsData } = useGetTrainingRounds(Number(modelId));

  const { mutateAsync: updateModelMutate, isLoading: isUpdating } =
    useUpdateModel();
  const { mutateAsync: deleteTranscript } = useDeleteTranscriptCustomModel();
  const { mutateAsync: deleteCustomModel } = useDeleteCustomModel();
  const { mutateAsync: downloadDataset } = useDownloadDatasetCustomModel();
  const { mutateAsync: exportDataset } = useExportCustomModelDataset();
  const mutation = useRunCustomModel();
  // Poll the validate endpoint so the readiness card can render progress
  // even before the customer thinks to press Train. The card is
  // informational; hiding it on failure is fine.
  const { data: readiness, isLoading: isReadinessLoading } =
    useCustomModelReadiness(Number(modelId), Boolean(modelId));
  // Rounds used/remaining + per-round cost for the caller's active
  // subscription. Powers the ``RoundAllowanceCard`` and gates the Train
  // button when the yearly allowance is exhausted (#2211 criterion 5).
  const { data: allowance, isLoading: isAllowanceLoading } =
    useCurrentCustomModelAllowance();
  // Held in a ref so the (stable) onUploadComplete callback can reset the Uppy
  // instance without re-creating it on every render.
  const resetUploadRef = useRef<() => void>(() => {});
  const {
    uppy,
    isUploading,
    hasFiles,
    startUpload,
    reset: resetUpload
  } = useCreateDataset(Number(modelId), ({ ok, failed }) => {
    // Auto-close the dataset modal once everything uploaded cleanly. On partial
    // failure keep it open so the user can retry the failed files.
    if (ok > 0 && failed === 0) {
      resetUploadRef.current();
      setNewDatasetModal(false);
    }
  });
  resetUploadRef.current = resetUpload;
  const validateModelMutation = useValidateCustomModel();

  const breadcrumbs = buildCustomModelBreadcrumbTrail(data?.result?.name || '');

  useEffect(() => {
    if (data?.result) setName(data.result.name || '');
  }, [data]);

  const baseLanguageKey = data?.result?.base_language_key ?? '';
  const lastModified = data?.result?.last_modified
    ? formatDate(data.result.last_modified)
    : '';

  const fullLanguageName = useMemo(() => {
    if (!baseLanguageKey || !supportedLanguages) return baseLanguageKey;
    const languageCode = baseLanguageKey.toLowerCase();
    const languageData = supportedLanguages.result.find(
      lang => lang.key && lang.key.toLowerCase() === languageCode
    );
    return languageData ? languageData.name : baseLanguageKey;
  }, [baseLanguageKey, supportedLanguages]);

  const trainingStatusNumber = data?.result?.training_log?.training_status;

  // Next round number is one more than the highest completed round,
  // or 1 if no rounds exist yet.
  const nextRoundNumber = useMemo(() => {
    const rounds = roundsData?.result ?? [];
    if (rounds.length === 0) return 1;
    return Math.max(...rounds.map(r => r.round_number)) + 1;
  }, [roundsData]);

  // ---- dataset table columns
  const columns = useMemo<DataGridColumn<DatasetRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('name'),
        minWidth: 300,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'round_number',
        accessorKey: 'round_number',
        enableRowCopyOnClick: false,
        header: t('training-round-number'),
        minWidth: 100,
        cell: ({ getValue }) => {
          const v = getValue();
          return v != null ? (
            <Badge color="zinc">#{String(v)}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableRowCopyOnClick: false,
        header: t('status', { defaultValue: 'Status' }),
        minWidth: 140,
        cell: ({ row }) => (
          <DatasetStatusCell
            status={row.original.status}
            error={row.original.error}
          />
        )
      },
      {
        id: 'type',
        accessorKey: 'type',
        enableRowCopyOnClick: false,
        header: t('type'),
        minWidth: 120,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'duration',
        accessorKey: 'duration',
        enableRowCopyOnClick: false,
        header: t('duration'),
        minWidth: 120,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'url',
        accessorKey: 'url',
        enableRowCopyOnClick: false,
        header: t('URL'),
        minWidth: 220,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'start_time',
        accessorKey: 'start_time',
        enableRowCopyOnClick: false,
        header: t('start-time'),
        minWidth: 140,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'end_time',
        accessorKey: 'end_time',
        enableRowCopyOnClick: false,
        header: t('end-time'),
        minWidth: 140,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t]
  );

  // ---- round history table columns
  const roundColumns = useMemo<DataGridColumn<RoundRow>[]>(
    () => [
      {
        id: 'round_number',
        accessorKey: 'round_number',
        header: t('training-round-number'),
        minWidth: 90,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => `#${String(getValue() ?? '')}`
      },
      {
        id: 'training_status',
        accessorKey: 'training_status',
        header: t('status', { defaultValue: 'Status' }),
        minWidth: 150,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => <RoundStatusBadge status={Number(getValue())} />
      },
      {
        id: 'started_at',
        accessorKey: 'started_at',
        header: t('training-round-started'),
        minWidth: 160,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? formatDate(v) : '—';
        }
      },
      {
        id: 'finished_at',
        accessorKey: 'finished_at',
        header: t('training-round-finished'),
        minWidth: 160,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? formatDate(v) : '—';
        }
      },
      {
        id: 'wer',
        accessorKey: 'wer',
        header: t('training-round-wer'),
        minWidth: 90,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => {
          const v = getValue() as number | null;
          return v != null ? `${(v * 100).toFixed(1)}%` : '—';
        }
      },
      {
        id: 'data_count',
        accessorKey: 'data_count',
        header: t('training-round-files'),
        minWidth: 90,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => {
          const v = getValue() as number | null;
          return v != null ? String(v) : '—';
        }
      },
      {
        id: 'asr_version',
        accessorKey: 'asr_version',
        header: t('training-round-artifact'),
        minWidth: 120,
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ?? '—';
        }
      }
    ],
    [t]
  );

  const typeMapping: Record<CustomModelDataType, string> = {
    [CustomModelDataType.TRANSCRIPT]: t('TRANSCRIPT'),
    [CustomModelDataType.TEST]: t('TEST'),
    [CustomModelDataType.AUDIO]: t('AUDIO'),
    [CustomModelDataType.MANIFEST]: t('MANIFEST')
  };
  const getDatasetTypeLabel = (type: number) =>
    typeMapping[type as CustomModelDataType] ?? String(type);

  const rows: DatasetRow[] = useMemo(() => {
    if (!customModelData) return [];
    return customModelData.result.map((item: ModelData) => ({
      id: item.id,
      name: item.name,
      type: getDatasetTypeLabel(item.type),
      language_id: item.language_id,
      media_url: item.media_url,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      duration: (item as any).duration ?? null,
      url: item.url,
      start_time: item.start_time ?? null,
      end_time: item.end_time ?? null,
      // Rows predating the status column come back without it → treat as READY.
      status: item.status ?? CustomModelDataStatus.READY,
      error: item.error ?? null,
      round_number: item.round_number ?? null
    }));
  }, [customModelData, t]);

  const roundRows: RoundRow[] = useMemo(() => {
    if (!roundsData) return [];
    return roundsData.result.map((r: TrainingRound) => ({
      id: r.id,
      round_number: r.round_number,
      training_status: r.training_status,
      started_at: r.started_at,
      finished_at: r.finished_at,
      asr_version: r.asr_version,
      wer: r.wer,
      log_name: r.log_name,
      data_count: r.data_count
    }));
  }, [roundsData]);

  // Row-level contribution data (pre-filter derives happen inside
  // ContributionsList). We need three things at the page level: whether the
  // model has any contributions (drives the acknowledgement + export flow),
  // the count for the delete-confirmation message, and the last completed
  // round number for the "pending" indicator on new clips.
  const contributionRows = useMemo(
    () =>
      (contributionsData?.result ?? []).filter(
        (r: ModelData) =>
          isContribution(r.source_kind) && r.type === CustomModelDataType.AUDIO
      ),
    [contributionsData]
  );
  const contributionsCount = contributionRows.length;
  const hasContributions = contributionsCount > 0;
  const lastCompletedRound = useMemo(() => {
    // A round with training_status = 4 is SUCCESS. Highest such number is
    // the last completed round.
    const successRounds = (roundsData?.result ?? []).filter(
      r => r.training_status === 4
    );
    if (successRounds.length === 0) return null;
    return Math.max(...successRounds.map(r => r.round_number));
  }, [roundsData]);

  const handleCellClick = (row: DatasetRow) => {
    setSelectedDatasetId(row.id);
  };

  // #2211: whole-dataset export presented as "the thing to do before
  // deleting a model". Signed URLs come back one call, then a helper
  // sequences the browser downloads so the browser's simultaneous-download
  // throttle does not silently drop half.
  const handleExportDataset = async () => {
    if (!modelId || isExporting) return;
    setIsExporting(true);
    try {
      const result = await exportDataset(Number(modelId));
      if (!result.files.length) {
        toast(t('export-empty'));
        return;
      }
      await downloadDatasetExport(result.files);
      toast.success(t('export-started', { count: result.files.length }));
    } catch (error) {
      const message =
        (error as AxiosError<BaseError>)?.response?.data?.message ||
        (error as AxiosError<BaseError>)?.response?.data?.error_description ||
        t('export-failed');
      toast.error(message);
    } finally {
      setIsExporting(false);
    }
  };

  // #2211: deleting a model with contributions requires acknowledgement.
  // The API returns 409 ``unacknowledged-contributions`` unless the query
  // string carries it — that guarantee is enforced server-side, this UI is
  // the surface where the customer sees what they are giving up.
  const handleDeleteModel = async () => {
    if (!modelId || isDeletingModel) return;
    if (hasContributions && !acknowledgeContributions) return;
    setIsDeletingModel(true);
    try {
      await deleteCustomModel({
        modelId: Number(modelId),
        acknowledgeContributions: hasContributions
          ? acknowledgeContributions
          : undefined
      });
      toast.success(t('custom-model-deleted'));
      navigate(PATHS().CUSTOM_MODELS);
    } catch (error) {
      const message =
        (error as AxiosError<BaseError>)?.response?.data?.message ||
        (error as AxiosError<BaseError>)?.response?.data?.error_description ||
        t('custom-model-delete-failed');
      toast.error(message);
    } finally {
      setIsDeletingModel(false);
    }
  };

  // Delete-one-contribution. The endpoint already removes the storage
  // object (api-scriptix #4020) and the transcript pair, so this is a
  // straight call to the same hook the uploads use.
  const handleDeleteContribution = async (row: ModelData) => {
    if (!modelId) return;
    setIsDeleting(true);
    try {
      await deleteTranscript({
        modelId: Number(modelId),
        datasetId: row.id
      });
      toast.success(t('contribution-removed'));
    } catch (error) {
      const message =
        (error as AxiosError<BaseError>)?.response?.data?.message ||
        (error as AxiosError<BaseError>)?.response?.data?.error_description ||
        t('an-error-occurred');
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const originalName = data?.result?.name ?? '';
  const hasChanges = name.trim() !== originalName.trim();
  const canSave = hasChanges && name.trim().length > 0;

  const handleSave = () => {
    if (!data || !organization) return;
    if (!canSave) return;
    updateModelMutate(
      {
        modelId: Number(modelId),
        body: {
          name,
          language_id: Number(data.result.base_language_id),
          organization_id: Number(organization.id)
        }
      },
      {
        onSuccess: () => {
          toast.success(t('custom-model-updated-successfully'));
        },
        onError: (error: AxiosError<BaseError>) => {
          const fallback = t('an-error-occurred-while-updating-model');
          const payload = error.response?.data;
          const message =
            payload?.message || payload?.error_description || fallback;
          toast.error(message);
        }
      }
    );
  };

  // Constrain which files the Dashboard accepts based on the selected slot.
  // 'auto' accepts everything; an explicit type narrows to its extensions.
  useEffect(() => {
    const allowed =
      datasetType === CustomModelDataType.AUDIO
        ? [...SUPPORTED_AUDIO_FORMATS, ...SUPPORTED_VIDEO_FORMATS]
        : datasetType === CustomModelDataType.TRANSCRIPT ||
            datasetType === CustomModelDataType.TEST
          ? SUPPORTED_TRANSCRIPT_FORMATS
          : null; // auto → keep the instance default (all formats)
    if (allowed) {
      uppy.setOptions({ restrictions: { allowedFileTypes: allowed } });
    }
  }, [uppy, datasetType]);

  const closeDatasetModal = () => {
    if (isUploading) return;
    resetUpload();
    setNewDatasetModal(false);
  };

  const handleUploadDataset = async () => {
    if (isUploading || !hasFiles) return;
    try {
      await startUpload(datasetType === 'auto' ? undefined : datasetType);
    } catch {
      // per-file errors surface in the Dashboard; summary toast in the hook
    }
  };

  // Kick off the actual training run (claims + notify trainer).
  const runTraining = async () => {
    setIsTraining(true);
    try {
      await mutation.mutateAsync(Number(modelId));
      toast.success(t('training-started-successfully'));
    } catch {
      // useRunCustomModel.onError already surfaces a specific toast
      // (FastAPI detail array, response.message, etc.).
    } finally {
      setTrainTheLanguageModal(false);
      setSkipConfirmModal(false);
      setSkippedFiles([]);
      setIsTraining(false);
    }
  };

  const handleStartTrainingLanguage = async () => {
    if (!data) return;
    setIsTraining(true);
    try {
      // Validation now happens at train time: it returns whether the dataset is
      // trainable plus the list of audio files with no matching transcript.
      const result = await validateModelMutation.mutateAsync(Number(modelId));
      if (!result.valid) {
        // ``blocked`` holds the reasons that stop training (processing,
        // missing or archived files); ``errors`` alone can miss them.
        toast.error(
          result.blocked?.[0] ?? result.errors?.[0] ?? t('validation_failed')
        );
        setTrainTheLanguageModal(false);
        return;
      }
      // Some audio has no transcript — confirm before training (they're skipped).
      if (result.skipped.length > 0) {
        setSkippedFiles(result.skipped);
        setTrainTheLanguageModal(false);
        setSkipConfirmModal(true);
        return;
      }
      await runTraining();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error_description ||
        t('an-error-occurred');
      toast.error(message);
      setTrainTheLanguageModal(false);
    } finally {
      setIsTraining(false);
    }
  };

  const statusMapping: Record<number, string> = {
    1: t('not-running'),
    // Status 2 is "ready to run" server-side; surfaced to users as
    // "Provisioning Resources" while the training environment is prepared.
    2: t('provisioning-resources'),
    3: t('running'),
    4: t('success'),
    5: t('failed')
  };
  const statusBadgeColorMapping: Record<
    number,
    'zinc' | 'amber' | 'sky' | 'emerald' | 'red'
  > = {
    1: 'zinc',
    2: 'amber',
    3: 'sky',
    4: 'emerald',
    5: 'red'
  };
  const trainingStatusText = trainingStatusNumber
    ? statusMapping[trainingStatusNumber] || t('not_available')
    : t('not_available');
  const trainingStatusBadgeColor = trainingStatusNumber
    ? (statusBadgeColorMapping[trainingStatusNumber] ?? 'zinc')
    : 'zinc';

  const getStatusHelpMessage = () => {
    const modelName = data?.result?.name || '';
    switch (trainingStatusNumber) {
      case 1: // not-running
      case 5: // failed
        return t('custom-model-status-not-running-help');
      case 2: // ready-to-run
        return t('custom-model-status-ready-help');
      case 3: // running
        return t('custom-model-status-running-help');
      case 4: // success
        // If there's already at least one completed round, hint that retrain is available.
        return roundRows.length > 0
          ? t('custom-model-status-success-retrain-help')
          : t('custom-model-status-success-help', { modelName });
      default:
        return t('custom-model-status-not-running-help');
    }
  };

  // Show "Train again" for SUCCESS (4); show "Train" for NOT_RUNNING (1) / FAILED (5).
  // Hidden entirely for READY_TO_RUN (2) and RUNNING (3).
  const showTrainButton =
    trainingStatusNumber !== 2 && trainingStatusNumber !== 3;
  const isRetrain = trainingStatusNumber === 4;

  // The yearly training allowance gates Train once it is exhausted (#2211,
  // criterion 5). Null ``limit`` means the plan is unlimited so the gate is
  // dormant; while the query is still loading we do not block the button
  // because a network blip must not lock training out.
  const trainingAllowanceExhausted =
    !!allowance && allowance.limit != null && (allowance.remaining ?? 0) <= 0;
  // A file still being processed has a row but no audio yet, so training on
  // it would fail or silently leave it out. The API refuses too; this keeps
  // the button honest. Reads the all-rows fetch rather than the paged grid so
  // a processing file on page two still counts, and that fetch polls while
  // anything is processing, so the button re-enables on its own.
  const filesProcessing = (
    contributionsData?.result ??
    customModelData?.result ??
    []
  ).some(row => row.status === CustomModelDataStatus.PROCESSING);
  const trainDisabled =
    isUpdating || trainingAllowanceExhausted || filesProcessing;

  const datasetActions = useMemo<GridAction<DatasetRow>[]>(
    () => [
      {
        id: 'download',
        label: t('download'),
        icon: <Download className="h-3.5 w-3.5" />,
        run: async row => {
          try {
            await downloadDataset({
              modelId: Number(modelId),
              datasetId: row.id,
              filename: row.name
            });
          } catch {
            toast.error(t('download-failed'));
          }
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: row => {
          setSelectedDatasetId(row.id);
          setDeleteModalOpen(true);
        }
      }
    ],
    [t, downloadDataset, modelId]
  );

  // Tab set for the model page (#2211). Kept close to the render so each
  // tab's JSX still reads top-to-bottom with the rest of the page. Ordered
  // left-to-right along the workflow: at-a-glance status, add data, review
  // corrections, review rounds.
  const overviewTabs: Tab[] = useMemo(
    () => [
      {
        id: 0,
        name: t('overview'),
        component: (
          <div className="pt-3">
            {/* Readiness + round allowance. Both visible before Train,
                always. Readiness shows the dataset side; allowance shows
                the billing side. When the yearly cap is exhausted the
                Train button is disabled with the reason stated inline. */}
            <div className="px-4 lg:px-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ReadinessCard
                readiness={readiness}
                loading={isReadinessLoading}
              />
              <RoundAllowanceCard
                allowance={allowance}
                loading={isAllowanceLoading}
              />
            </div>
          </div>
        )
      },
      {
        id: 1,
        name: t('uploads'),
        component: (
          <div className="pt-3">
            {isLoadingData ? (
              <div className="w-full flex justify-center items-center pt-20">
                <Loader height={10} width={3} />
              </div>
            ) : (
              <>
                <PageHeader
                  title={
                    <div className="flex items-center gap-2">
                      <span>{t('datasets')}</span>
                      <InfoTooltip
                        content={t('custom-model-dataset-types-info')}
                      />
                    </div>
                  }
                  caption={t('manage-datasets-and-training-files')}
                >
                  <Button
                    onClick={() => setNewDatasetModal(true)}
                    icon={<Plus width={16} />}
                    size="small"
                    label={t('new-dataset')}
                    variant="secondary"
                    className="w-fit"
                  />
                </PageHeader>
                <div className="px-4 lg:px-0 py-4 sm:pb-0">
                  <DataGridV2<DatasetRow>
                    data={rows}
                    columns={columns}
                    enableRowSelection={false}
                    enablePagination
                    manualPagination
                    enableSorting={false}
                    manualSorting={false}
                    enableFiltering={false}
                    manualFiltering={false}
                    totalItems={customModelData?.total_results ?? 0}
                    pageSize={25}
                    pageSizeOptions={[25, 50, 75, 100]}
                    showColumnFilters={false}
                    filterMode="popover"
                    height="36vh"
                    dense
                    grid={false}
                    striped
                    loading={isLoadingData || isFetching || !customModelData}
                    getRowId={row => String(row.id)}
                    onCellClick={handleCellClick}
                    actions={datasetActions}
                  />
                </div>
              </>
            )}
          </div>
        )
      },
      {
        id: 2,
        name: (
          <span className="inline-flex items-center gap-2">
            {t('contributions')}
            <Badge color="current">{contributionsCount}</Badge>
          </span>
        ),
        component: (
          <div className="pt-3">
            <PageHeader
              title={t('contributions')}
              caption={t('contributions-caption')}
            />
            <div className="px-4 lg:px-0 py-4 sm:pb-0">
              <ContributionsList
                rows={contributionsData?.result ?? []}
                lastCompletedRound={lastCompletedRound}
                onDelete={handleDeleteContribution}
                isDeleting={isDeleting}
              />
            </div>
          </div>
        )
      },
      {
        id: 3,
        name: t('rounds'),
        component: (
          <div className="pt-3">
            <PageHeader
              title={t('training-round-history')}
              caption={t('training-round-history-caption')}
            />
            <div className="px-4 lg:px-0 py-4 sm:pb-0">
              {roundRows.length === 0 ? (
                <Card padding="none" className="p-6">
                  <Text as="p" size="sm" tone="muted">
                    {t('training-round-history-empty')}
                  </Text>
                </Card>
              ) : (
                <DataGridV2<RoundRow>
                  data={roundRows}
                  columns={roundColumns}
                  enableRowSelection={false}
                  enablePagination
                  manualPagination={false}
                  enableSorting={false}
                  manualSorting={false}
                  enableFiltering={false}
                  manualFiltering={false}
                  totalItems={roundRows.length}
                  pageSize={25}
                  pageSizeOptions={[25, 50, 75, 100]}
                  showColumnFilters={false}
                  filterMode="popover"
                  height="36vh"
                  dense
                  grid={false}
                  striped
                  loading={false}
                  getRowId={row => String(row.id)}
                />
              )}
            </div>
          </div>
        )
      }
    ],
    [
      t,
      readiness,
      isReadinessLoading,
      allowance,
      isAllowanceLoading,
      isLoadingData,
      isFetching,
      customModelData,
      rows,
      columns,
      datasetActions,
      contributionsCount,
      lastCompletedRound,
      handleDeleteContribution,
      isDeleting,
      roundRows,
      roundColumns
    ]
  );

  return (
    <div className="bg-app h-full min-h-0 space-y-2 overflow-y-auto pb-8">
      <PageHeader
        title={
          data?.result ? (
            <Breadcrumbs
              items={breadcrumbs.slice(0, -1)}
              currentFolderName={data.result.name}
              rootLabel="Custom Models"
              rootPath={PATHS().CUSTOM_MODELS}
            />
          ) : (
            t('custom-modal-details')
          )
        }
        caption={t('view-and-manage-custom-model-details')}
      >
        {showTrainButton && (
          <Button
            icon={<RefreshCcw width={16} />}
            size="small"
            label={
              <span className="hidden sm:inline">
                {isRetrain ? t('train-again') : t('train-the-language')}
              </span>
            }
            variant="primary"
            className="px-2 mr-2"
            onClick={() => setTrainTheLanguageModal(true)}
            disabled={trainDisabled}
            title={
              trainingAllowanceExhausted
                ? t('round-allowance-exhausted-help')
                : filesProcessing
                  ? t('train-disabled-files-processing')
                  : isRetrain
                    ? t('train-again')
                    : t('train-the-language')
            }
          />
        )}
        <Button
          icon={<Save width={16} />}
          size="small"
          label={<span className="hidden sm:inline">{t('update')}</span>}
          variant="secondary"
          className="px-2"
          onClick={handleSave}
          disabled={isUpdating || !canSave}
          title={t('update')}
        />
        <Button
          icon={<FileDown width={16} />}
          size="small"
          label={
            <span className="hidden sm:inline">
              {isExporting ? t('exporting') : t('export-dataset')}
            </span>
          }
          variant="secondary"
          className="px-2 ml-2"
          onClick={handleExportDataset}
          disabled={isExporting || (rows.length === 0 && !hasContributions)}
          title={t('export-dataset')}
        />
        <Button
          icon={<Trash2 width={16} />}
          size="small"
          label={<span className="hidden sm:inline">{t('delete-model')}</span>}
          variant="danger"
          className="px-2 ml-2"
          onClick={() => {
            // Every time the dialog opens, require the acknowledgement to
            // be re-checked. Keeping it "checked" across opens would defeat
            // the point of the confirmation.
            setAcknowledgeContributions(false);
            setDeleteModelModalOpen(true);
          }}
          disabled={isDeletingModel}
          title={t('delete-model')}
        />
      </PageHeader>

      {/* Confirm dialog — differs for first run vs. retrain */}
      {isRetrain ? (
        <ConfirmationModal
          isOpen={trainTheLanguageModal}
          onClose={() => setTrainTheLanguageModal(false)}
          onConfirm={handleStartTrainingLanguage}
          size="small"
          title={t('train-again-confirmation')}
          caption={t('train-again-confirmation-caption', {
            round: nextRoundNumber,
            modelName: data?.result?.name?.trim() || t('this-model')
          })}
          confirmLabel={isTraining ? t('training') : t('train-again')}
          cancelLabel={t('cancel')}
          confirmDisabled={isTraining}
          destructive={false}
          autoFocusConfirm
        />
      ) : (
        <ConfirmationModal
          isOpen={trainTheLanguageModal}
          onClose={() => setTrainTheLanguageModal(false)}
          onConfirm={handleStartTrainingLanguage}
          size="small"
          title={t('train-the-language-confirmation')}
          caption={t('train-the-language-confirmation-caption', {
            testName: data?.result?.name?.trim() || t('this-model')
          })}
          confirmLabel={isTraining ? t('training') : t('train')}
          cancelLabel={t('cancel')}
          confirmDisabled={isTraining}
          destructive={false}
          autoFocusConfirm
        />
      )}

      <ConfirmationModal
        isOpen={skipConfirmModal}
        onClose={() => {
          if (!isTraining) {
            setSkipConfirmModal(false);
            setSkippedFiles([]);
          }
        }}
        onConfirm={runTraining}
        size="small"
        title={t('train-skip-files-title')}
        caption={t('train-skip-files-caption', { count: skippedFiles.length })}
        confirmLabel={isTraining ? t('training') : t('train-anyway')}
        cancelLabel={t('cancel')}
        confirmDisabled={isTraining}
        destructive={false}
        autoFocusConfirm
      />

      {/* Delete-model dialog (#2211). Contributed clips are hand-curated
          from sessions that may already have aged out; the acknowledgement
          check-box is the customer's chance to see what they are about to
          destroy and offers the export first. When the model holds no
          contributions the dialog collapses to a plain confirmation. */}
      <ConfirmationModal
        isOpen={deleteModelModalOpen}
        onClose={() => {
          if (!isDeletingModel) setDeleteModelModalOpen(false);
        }}
        onConfirm={handleDeleteModel}
        size={hasContributions ? 'medium' : 'small'}
        title={t('delete-model-confirm-title')}
        caption={
          hasContributions
            ? t('delete-model-confirm-caption-contributions', {
                count: contributionsCount
              })
            : t('delete-model-confirm-caption')
        }
        confirmLabel={isDeletingModel ? t('deleting') : t('delete-model')}
        cancelLabel={t('cancel')}
        confirmDisabled={
          isDeletingModel || (hasContributions && !acknowledgeContributions)
        }
        destructive
      >
        {hasContributions && (
          <div className="space-y-3">
            <Button
              onClick={handleExportDataset}
              icon={<FileDown width={14} />}
              size="small"
              variant="secondary"
              label={
                isExporting ? t('exporting') : t('export-dataset-before-delete')
              }
              disabled={isExporting}
              className="w-fit"
            />
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={acknowledgeContributions}
                onChange={e => setAcknowledgeContributions(e.target.checked)}
                disabled={isDeletingModel}
                aria-label={t('acknowledge-contributions-loss')}
              />
              <span>{t('acknowledge-contributions-loss')}</span>
            </label>
          </div>
        )}
      </ConfirmationModal>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 py-4 px-4 lg:px-0">
        <Card padding="none" className="p-3">
          <dt>
            <Text as="span" size="xs" className="px-1 tracking-wide">
              {t('name')}
            </Text>
          </dt>
          <dd className="mt-1">
            <Input
              id="name"
              value={name}
              onChange={e => setName(e.target.value)}
              type="text"
              autoComplete="given-name"
              className="h-10 w-full text-sm"
            />
          </dd>
        </Card>

        <Card padding="none" className="p-3">
          <dt>
            <Text as="span" size="xs" className="tracking-wide">
              {t('training-status')}
            </Text>
          </dt>
          <dd className="mt-2.5 min-w-0">
            <Badge
              color={trainingStatusBadgeColor}
              className="inline-flex items-center gap-2"
            >
              <span className="inline-block h-2 w-2 rounded-full bg-current opacity-70" />
              {trainingStatusText}
            </Badge>
          </dd>
        </Card>

        <Card padding="none" className="p-3">
          <dt>
            <Text as="span" size="xs" className="tracking-wide">
              {t('base-language-model')}
            </Text>
          </dt>
          <dd className="mt-2.5 min-w-0">
            <Text as="p" size="sm" weight="medium" className="truncate">
              {fullLanguageName}
            </Text>
          </dd>
        </Card>

        <Card padding="none" className="p-3">
          <dt>
            <Text as="span" size="xs" className="tracking-wide">
              {t('last-modified')}
            </Text>
          </dt>
          <dd className="mt-2.5 min-w-0">
            <Text as="p" size="sm" weight="medium" className="truncate">
              {lastModified}
            </Text>
          </dd>
        </Card>
      </div>

      <div className="px-4 lg:px-0">
        <InfoBanner
          title={t('training-status') + ': ' + trainingStatusText}
          defaultExpanded={trainingStatusNumber === 4}
        >
          <p className="mb-3">{getStatusHelpMessage()}</p>

          <div className="mt-4 pt-3 border-t border-hairline">
            <p className="font-medium mb-2">
              {t('custom-model-need-help-preparing')}
            </p>
            <button
              onClick={() => navigate(PATHS().HOME)}
              className="underline font-medium hover:opacity-80"
            >
              <Text as="span" tone="brand">
                {t('custom-model-use-force-alignment-link')}
              </Text>
            </button>
            <Text as="p" size="xs" tone="muted" className="mt-2">
              {t('custom-model-file-requirements')}
            </Text>
          </div>
        </InfoBanner>
      </div>

      {/* Modals live outside the tab shell so they can open regardless of
          which tab is active (e.g. the delete-model action is in the page
          header and applies to the whole model, not a tab). */}
      <Modal
        isOpen={newDatasetModal}
        onClose={closeDatasetModal}
        size="small"
        title={
          data?.result?.name
            ? `${t('new-dataset')} — ${data.result.name}`
            : t('new-dataset')
        }
        caption={t('create-dataset-for-custom-model')}
        footerActions={[
          <Button
            onClick={closeDatasetModal}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isUploading}
          />,
          <Button
            onClick={handleUploadDataset}
            label={isUploading ? t('uploading') : t('create')}
            size="small"
            variant="primary"
            disabled={isUploading || !hasFiles}
          />
        ]}
      >
        <div className="mt-1">
          <div className="grid grid-cols-12 gap-4 mb-4 border-b border-hairline pb-4 items-center">
            <Text
              as="label"
              size="sm"
              weight="medium"
              className="col-span-2 mb-1"
            >
              {t('type')}
            </Text>
            <div className="col-span-10">
              <div
                className={isUploading ? 'opacity-50 pointer-events-none' : ''}
              >
                <SelectInput
                  value={datasetType}
                  onChange={opt =>
                    setDatasetType(opt.value as CustomModelDataType | 'auto')
                  }
                  options={[
                    { label: t('auto-detect'), value: 'auto' },
                    {
                      label: t('TRANSCRIPT'),
                      value: CustomModelDataType.TRANSCRIPT
                    },
                    { label: t('AUDIO'), value: CustomModelDataType.AUDIO }
                  ]}
                  placeholder={t('select-type')}
                  size="small"
                />
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <Text
              as="label"
              size="sm"
              weight="semibold"
              className="block mt-4 mb-2"
            >
              {datasetType === 'auto'
                ? t('upload-files')
                : datasetType === CustomModelDataType.AUDIO
                  ? t('upload-audio-files')
                  : t('upload-transcript-files')}{' '}
              ({t('up-to-files', { count: MAX_DATASET_FILES_PER_BATCH })}):
            </Text>
            {/* Resumable TUS uploads with per-file progress, pause/resume and
                retry, driven by the persistent Uppy instance. */}
            <UppyDashboard
              uppy={uppy}
              plugins={[]}
              width="100%"
              height={280}
              note={t('file-validation-note')}
            />
          </div>
        </div>
      </Modal>

      {selectedDatasetId && (
        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          size="small"
          title={t('delete-confirmation')}
          caption={t(
            'are-you-sure-you-want-to-delete-this-file-this-action-cannot-be-undone'
          )}
          destructive
          confirmLabel={isDeleting ? t('deleting') : t('delete')}
          confirmDisabled={isDeleting}
          onConfirm={async () => {
            if (!selectedDatasetId) return;
            setIsDeleting(true);
            try {
              await deleteTranscript({
                modelId: Number(modelId),
                datasetId: selectedDatasetId
              });
              toast.success(t('file-deleted-successfully'));
              setDeleteModalOpen(false);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
              const data = error?.response?.data;
              const messages = Array.isArray(data?.detail)
                ? data.detail.map(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (e: any) => `${e.loc?.[1] ?? 'field'}: ${e.msg}`
                  )
                : [
                    data?.message ||
                      data?.error_description ||
                      t('an-error-occurred')
                  ];
              toast.error(messages.join('\n'));
            } finally {
              setIsDeleting(false);
            }
          }}
        />
      )}

      {/* Tabbed content (#2211). Splitting the page into Overview / Uploads /
          Contributions / Rounds keeps each surface short enough to be
          scanned at a glance, and lines the workflow up left-to-right:
          check readiness, add data, review contributions, review rounds. */}
      <Tabs
        searchParams
        minHeight="auto"
        maxHeight="none"
        tabs={overviewTabs}
      />
    </div>
  );
};
